import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const GuessingLeaderboard = ({ tournamentId, darkMode = false }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultsPublished, setResultsPublished] = useState(false);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);

        // Check if results are published
        const resultsQuery = query(
          collection(db, 'guessingResults'),
          where('tournamentId', '==', tournamentId)
        );
        const resultsSnapshot = await getDocs(resultsQuery);

        if (resultsSnapshot.empty || !resultsSnapshot.docs[0].data().isPublished) {
          setResultsPublished(false);
          setLoading(false);
          return;
        }

        setResultsPublished(true);
        const resultsData = resultsSnapshot.docs[0].data();

        // Get all user guesses for this tournament
        const guessesQuery = query(
          collection(db, 'playerGuesses'),
          where('tournamentId', '==', tournamentId)
        );
        const guessesSnapshot = await getDocs(guessesQuery);

        if (guessesSnapshot.empty) {
          setLeaderboard([]);
          setLoading(false);
          return;
        }

        // Calculate correct guesses for each user
        const userScores = [];

        for (const guessDoc of guessesSnapshot.docs) {
          const guessData = guessDoc.data();
          const userId = guessData.userId;

          // Skip if no guesses array
          if (!guessData.guesses || !Array.isArray(guessData.guesses)) {
            continue;
          }

          // Get user details
          let userData = { username: 'Unknown User' };
          try {
            const userDoc = await getDoc(doc(db, 'userProfiles', userId));
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          } catch (e) {
            console.error('Error fetching user:', e);
          }

          // Count correct guesses
          let correctCount = 0;

          for (const guess of guessData.guesses) {
            const correctPlayer = resultsData.correctGuesses.find(
              (cg) => cg.anonymousNameId === guess.anonymousNameId
            );

            if (correctPlayer && correctPlayer.actualPlayerId === guess.guessedPlayerId) {
              correctCount++;
            }
          }

          userScores.push({
            userId,
            username: userData.username,
            profilePicUrl: userData.profilePicUrl,
            correctGuesses: correctCount,
            totalGuesses: guessData.guesses.length,
          });
        }

        // Sort by correct guesses (descending)
        userScores.sort((a, b) => b.correctGuesses - a.correctGuesses);

        // Take top 10
        setLeaderboard(userScores.slice(0, 10));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setError('Failed to load leaderboard data');
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [tournamentId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress size={30} sx={{ color: darkMode ? 'white' : undefined }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          mb: 3,
          bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : undefined,
          color: darkMode ? 'white' : undefined,
          '.MuiAlert-icon': { color: darkMode ? 'white' : undefined },
        }}
      >
        {error}
      </Alert>
    );
  }

  if (!resultsPublished) {
    return null;
    // return (
    //   <Alert
    //     severity="info"
    //     sx={{
    //       mb: 3,
    //       bgcolor: darkMode ? 'rgba(2, 136, 209, 0.2)' : undefined,
    //       color: darkMode ? 'white' : undefined,
    //       '.MuiAlert-icon': { color: darkMode ? 'white' : undefined }
    //     }}
    //   >
    //     The guessing game results haven't been published yet. Stay tuned!
    //   </Alert>
    // );
  }

  return (
    <Box mt={3}>
      <Typography
        variant="h5"
        component="h3"
        gutterBottom
        sx={{ color: darkMode ? 'white' : 'text.primary' }}
      >
        Top Guessers
      </Typography>

      {leaderboard.length === 0 ? (
        <Alert
          severity="info"
          sx={{
            bgcolor: darkMode ? 'rgba(2, 136, 209, 0.2)' : undefined,
            color: darkMode ? 'white' : undefined,
            '.MuiAlert-icon': { color: darkMode ? 'white' : undefined },
          }}
        >
          No users have submitted guesses yet.
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : undefined,
            boxShadow: darkMode ? 'none' : undefined,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  }}
                >
                  Rank
                </TableCell>
                <TableCell
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  }}
                >
                  User
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  }}
                >
                  Correct Guesses
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  }}
                >
                  Total Guesses
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                  }}
                >
                  Success Rate
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboard.map((user, index) => (
                <TableRow key={user.userId}>
                  <TableCell
                    sx={{
                      color: darkMode ? 'white' : undefined,
                      borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: darkMode ? 'white' : undefined,
                      borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                    }}
                  >
                    <Link
                      href={`/user/${user.userId}`}
                      underline="hover"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: darkMode ? 'white' : 'primary.main',
                        '&:hover': {
                          color: darkMode ? 'rgba(255, 255, 255, 0.8)' : 'primary.dark',
                        },
                      }}
                    >
                      {user.profilePicUrl && (
                        <Box
                          component="img"
                          src={user.profilePicUrl}
                          alt={user.username}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                      )}
                      {user.username}
                    </Link>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: darkMode ? 'white' : undefined,
                      borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                    }}
                  >
                    {user.correctGuesses}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: darkMode ? 'white' : undefined,
                      borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                    }}
                  >
                    {user.totalGuesses}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: darkMode ? 'white' : undefined,
                      borderBottomColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
                    }}
                  >
                    {Math.round((user.correctGuesses / user.totalGuesses) * 100)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default GuessingLeaderboard;
